import * as React from 'react';
import { Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { AbsencePeriod, AbsencePeriodUser, BookingView } from 'cb-model';
import {
  addControlColumn,
  Display,
  List,
  NavIconContainer,
  PageTitle,
  QueryMulti,
  QuerySingle,
  Section,
  useCacheRecord,
} from 'gossamer-front-end';
import { displayLocaleDate } from 'gossamer-unified';

export const AbsencePeriodUserDisplay = (): React.JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();

  // Fetch the AbsencePeriodUser record to get the user_id and absence_period_id
  const { data: absencePeriodUser } = useCacheRecord(AbsencePeriodUser, params.key);

  // Fetch the related AbsencePeriod to get date range
  const { data: absencePeriod } = useCacheRecord(
    AbsencePeriod,
    absencePeriodUser?.absence_period_id ? String(absencePeriodUser.absence_period_id) : null,
    !absencePeriodUser?.absence_period_id,
  );

  const eventIds = (absencePeriod?.event_ids || '').split(',').map((id) => parseInt(id, 10));

  return (
    <>
      <NavIconContainer entityId={AbsencePeriodUser.id} />
      <PageTitle
        title="Absence Period Detail for User"
        subtitle="Absence tracking details for this user during the specified period"
      />

      <Section>
        <QuerySingle entity={AbsencePeriodUser} keyValue={params.key} render={Display} />
      </Section>

      {absencePeriodUser && absencePeriod && eventIds && (
        <>
          <Section
            title="Absent"
            subtitle={`Events where the user was marked as absent (${displayLocaleDate(
              absencePeriod.start_date,
            )} to ${displayLocaleDate(absencePeriod.end_date)})`}
          >
            <QueryMulti
              entity={BookingView}
              alterEntity={(localEntity: typeof BookingView) => {
                localEntity.fields.user_id.listColumn = false;
                localEntity.fields.attendance_result.listColumn = false;
                localEntity.fields.computed_status.listColumn = false;
                localEntity.fields.absence_reason.listColumn = true;
                addControlColumn(
                  localEntity,
                  'update',
                  99,
                  (row) => String(row.attendance_id),
                  (props) => (
                    <Button size="sm" onClick={() => navigate(`/admin/attendance/update/${props.value}`)}>
                      Update
                    </Button>
                  ),
                );
              }}
              queryOptions={{
                where: {
                  and: [
                    { fieldId: 'user_id', operator: 'eq', value: absencePeriodUser.user_id },
                    { fieldId: 'attendance_result', operator: 'eq', value: 'absent' },
                    { fieldId: 'event_id', operator: 'in', value: eventIds },
                    { fieldId: 'booking_status', operator: 'eq', value: 'active' },
                  ],
                },
                order: [['start_at', 'DESC']],
              }}
              render={List}
            />
          </Section>

          <Section
            title="Attendance Not Recorded"
            subtitle={`Events where the user had a booking but attendance was not recorded (${displayLocaleDate(
              absencePeriod.start_date,
            )} to ${displayLocaleDate(absencePeriod.end_date)})`}
          >
            <QueryMulti
              entity={BookingView}
              alterEntity={(localEntity: typeof BookingView) => {
                localEntity.fields.user_id.listColumn = false;
                localEntity.fields.attendance_result.listColumn = false;
                localEntity.fields.computed_status.listColumn = false;
                addControlColumn(
                  localEntity,
                  'create',
                  99,
                  (row) => String(row.ticket_booking_id),
                  (props) => (
                    <Button size="sm" onClick={() => navigate(`/admin/attendance/create/${props.value}`)}>
                      Create
                    </Button>
                  ),
                );
              }}
              queryOptions={{
                where: {
                  and: [
                    { fieldId: 'user_id', operator: 'eq', value: absencePeriodUser.user_id },
                    { fieldId: 'attendance_id', operator: 'bl', value: '' },
                    { fieldId: 'event_id', operator: 'in', value: eventIds },
                    { fieldId: 'booking_status', operator: 'eq', value: 'active' },
                  ],
                },
                order: [['start_at', 'DESC']],
              }}
              render={List}
            />
          </Section>

          <Section
            title="Session Did Not Take Place"
            subtitle={`Events where this user recorded the session as not having occurred (${displayLocaleDate(
              absencePeriod.start_date,
            )} to ${displayLocaleDate(
              absencePeriod.end_date,
            )}) (NOTE: the system will still regard one of these as an absence if other users recorded attendance at the same event)`}
          >
            <QueryMulti
              entity={BookingView}
              alterEntity={(localEntity: typeof BookingView) => {
                localEntity.fields.user_id.listColumn = false;
                localEntity.fields.attendance_result.listColumn = false;
                localEntity.fields.computed_status.listColumn = false;
                localEntity.fields.absence_reason.listColumn = true;
                addControlColumn(
                  localEntity,
                  'update',
                  99,
                  (row) => String(row.attendance_id),
                  (props) => (
                    <Button size="sm" onClick={() => navigate(`/admin/attendance/update/${props.value}`)}>
                      Update
                    </Button>
                  ),
                );
              }}
              queryOptions={{
                where: {
                  and: [
                    { fieldId: 'user_id', operator: 'eq', value: absencePeriodUser.user_id },
                    { fieldId: 'attendance_result', operator: 'eq', value: 'noSession' },
                    { fieldId: 'event_id', operator: 'in', value: eventIds },
                    { fieldId: 'booking_status', operator: 'eq', value: 'active' },
                  ],
                },
                order: [['start_at', 'DESC']],
              }}
              render={List}
            />
          </Section>

          <Section
            title="Attended"
            subtitle={`Events where the user attended (${displayLocaleDate(
              absencePeriod.start_date,
            )} to ${displayLocaleDate(absencePeriod.end_date)})`}
          >
            <QueryMulti
              entity={BookingView}
              alterEntity={(localEntity: typeof BookingView) => {
                localEntity.fields.user_id.listColumn = false;
                localEntity.fields.attendance_result.listColumn = false;
                localEntity.fields.computed_status.listColumn = false;
                addControlColumn(
                  localEntity,
                  'update',
                  99,
                  (row) => String(row.attendance_id),
                  (props) => (
                    <Button size="sm" onClick={() => navigate(`/admin/attendance/update/${props.value}`)}>
                      Update
                    </Button>
                  ),
                );
              }}
              queryOptions={{
                where: {
                  and: [
                    { fieldId: 'user_id', operator: 'eq', value: absencePeriodUser.user_id },
                    { fieldId: 'attendance_result', operator: 'eq', value: 'attended' },
                    { fieldId: 'event_id', operator: 'in', value: eventIds },
                    { fieldId: 'booking_status', operator: 'eq', value: 'active' },
                  ],
                },
                order: [['start_at', 'DESC']],
              }}
              render={List}
            />
          </Section>
        </>
      )}
    </>
  );
};
